import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import type { FreeSwitchGroup } from '../api';
import { updateFreeSwitchGroup } from '../api';

interface FreeSwitchGroupListProps {
  groups: FreeSwitchGroup[];
  isLoading?: boolean;
  onGroupUpdated?: () => void;
}

export const FreeSwitchGroupList: React.FC<FreeSwitchGroupListProps> = ({
  groups,
  isLoading = false,
  onGroupUpdated,
}) => {
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);
  const [filterWizardEnabled, setFilterWizardEnabled] = useState(false);
  const [updatingGroupId, setUpdatingGroupId] = useState<number | null>(null);

  const filteredGroups = useMemo(() => {
    const sorted = [...groups].sort((a, b) => a.name.localeCompare(b.name));
    if (filterWizardEnabled) {
      return sorted.filter((g) => g.wizard_enabled);
    }
    return sorted;
  }, [groups, filterWizardEnabled]);

  const handleToggleWizard = async (groupId: number, enabled: boolean): Promise<void> => {
    setUpdatingGroupId(groupId);
    try {
      await updateFreeSwitchGroup(groupId, { wizard_enabled: enabled });
      onGroupUpdated?.();
    } catch (err) {
      console.error('Error updating group:', err);
    } finally {
      setUpdatingGroupId(null);
    }
  };

  const rows = filteredGroups;

  const toggleExpanded = (groupId: number): void => {
    setExpandedGroupId((prev) => (prev === groupId ? null : groupId));
  };

  if (isLoading && rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-white-1.0 p-8 text-sm text-gray-2.0">
        Cargando FreeSWITCH Groups…
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white-1.0">
      {/* Filter */}
      <div className="flex items-center gap-4 border-b border-white-2.0 px-4 py-2">
        <label className="flex items-center gap-2 text-sm text-gray-2.0 cursor-pointer">
          <input
            type="checkbox"
            checked={filterWizardEnabled}
            onChange={(e) => setFilterWizardEnabled(e.target.checked)}
            className="rounded border-gray-300 text-primary-2.0 focus:ring-primary-2.0"
          />
          Solo habilitados para wizard
        </label>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        {rows.length === 0 ? (
          <div className="flex h-full items-center justify-center p-8 text-sm text-gray-2.0">
            {filterWizardEnabled
              ? 'No hay grupos habilitados para wizard.'
              : 'No hay FreeSWITCH Groups configurados.'}
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 border-b border-white-2.0 bg-white-1.0">
              <tr>
                <th className="w-10 px-4 py-2.5" />
                <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  Nombre
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  FreeSWITCH URI
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  Wizard
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  Cuentas
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  Max
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  Disponibles
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  Creado
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((group) => {
                const isExpanded = expandedGroupId === group.id;
                return (
                  <React.Fragment key={group.id}>
                    <tr
                      tabIndex={0}
                      onClick={() => toggleExpanded(group.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpanded(group.id);
                        }
                      }}
                      className={classNames(
                        'cursor-pointer border-b border-white-2.0 transition-colors',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-2.0',
                        isExpanded
                          ? 'border-l-4 border-l-primary-2.0 bg-primary-2.0/10'
                          : 'bg-white-0.0 hover:bg-white-1.0'
                      )}
                    >
                      <td className="px-4 py-3 text-center">
                        <Icon
                          name={isExpanded ? OrbitIcons.ChevronDown : OrbitIcons.ChevronRight}
                          size={16}
                          className="text-gray-3.0"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-black-1.0">{group.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="font-mono text-sm text-black-1.0"
                          title={group.freeswitch_id}
                        >
                          {group.freeswitch_uri}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleWizard(group.id, !group.wizard_enabled);
                          }}
                          disabled={updatingGroupId === group.id}
                          className={classNames(
                            'inline-flex items-center justify-center rounded px-2 py-1 text-xs font-medium transition-colors',
                            group.wizard_enabled
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                            updatingGroupId === group.id && 'opacity-50 cursor-not-allowed'
                          )}
                        >
                          {updatingGroupId === group.id
                            ? '...'
                            : group.wizard_enabled
                            ? 'Sí'
                            : 'No'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={classNames(
                            'inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                            group.account_count > 0
                              ? 'bg-primary-2.0/10 text-primary-2.0'
                              : 'bg-gray-100 text-gray-500'
                          )}
                        >
                          {group.account_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm text-gray-2.0">{group.max_accounts}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={classNames(
                            'inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                            group.available_slots > 5
                              ? 'bg-green-100 text-green-700'
                              : group.available_slots > 0
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          )}
                        >
                          {group.available_slots}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-2.0">
                          {new Date(group.created_at).toLocaleDateString('es-PR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && group.accounts.length > 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-8 py-4">
                          <div className="rounded-lg border border-white-2.0 bg-white-0.0 overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-white-1.0">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                                    ID
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                                    Nombre
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                                    Cosmos Account ID
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.accounts.map((account) => (
                                  <tr key={account.id} className="border-t border-white-2.0">
                                    <td className="px-4 py-2 text-sm text-gray-2.0">
                                      {account.id}
                                    </td>
                                    <td className="px-4 py-2 text-sm font-medium text-black-1.0">
                                      {account.name}
                                    </td>
                                    <td className="px-4 py-2 font-mono text-xs text-gray-2.0">
                                      {account.cosmos_account_id}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    {isExpanded && group.accounts.length === 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-8 py-4 text-center text-sm text-gray-2.0">
                          Este grupo no tiene cuentas asignadas.
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
